import { Component, Input, OnInit, OnChanges, SimpleChanges, HostListener, ElementRef } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDateRangeInput } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatHint } from '@angular/material/form-field';
import { MatLabel } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MemberDashboardService } from '../member-dashboard.service';

interface MindfulnessCard {
  dateKey: string;
  startDate: string;
  completedDate: string | null;
  isCompleted: boolean;
  sessions: MindfulnessSession[];
}

interface SessionCompletion {
  id: string;
  createdDateTime: string;
}

interface MindfulnessSession {
  sessionNumber: number;
  title: string;
  thumbnail: string;
  completionDate: string | null;
  isCompleted: boolean;
  completions: SessionCompletion[]; // Array to store multiple completions
  showDropdown?: boolean; // Track dropdown visibility
}

@Component({
  selector: 'app-mindfulness-sessions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatDateRangeInput,
    MatNativeDateModule,
    MatInputModule,
    MatHint,
    MatLabel,
    MatIconModule
  ],
  templateUrl: './mindfulness-sessions.component.html',
  styleUrl: './mindfulness-sessions.component.css'
})
export class MindfulnessSessionsComponent implements OnInit, OnChanges {
  @Input() memberId: any;
  @Input() startDate: any;
  @Input() endDate: any;

  // Mindfulness Sessions tab date tracking
  previousDateMindfulness: any;
  currentDateMindfulness: any;
  startDateMindfulness: any = formatDate(new Date().setDate(new Date().getDate() - 7), 'yyyy-MM-dd', 'en');
  endDateMindfulness: any = formatDate(Date.now(), 'yyyy-MM-dd', 'en');
  mindfulnessCards: MindfulnessCard[] = [];
  filteredMindfulnessCards: MindfulnessCard[] = [];
  searchQuery: string = '';
  expandedCardIndex: number | null = null;
  statusFilter: string = 'all';
  isLoading: boolean = false;

  // Session configuration
  private readonly sessionConfig = [
    {
      number: 1,
      title: '"Arriving Here"-Mindful Breathing',
      thumbnail: 'assets/img/memberdashboard/Rectangle 4811784.png'
    },
    {
      number: 2,
      title: 'Belly Breathing',
      thumbnail: 'assets/img/memberdashboard/Rectangle 4811784 (1).png'
    },
    {
      number: 3,
      title: 'Present Moment - Breath, Body, Thoughts and Feelings',
      thumbnail: 'assets/img/memberdashboard/Rectangle 4811784 (2).png'
    },
    {
      number: 4,
      title: 'Thoughts & Wandering Mind - Thoughts and judgments',
      thumbnail: 'assets/img/memberdashboard/Rectangle 4811784 (3).png'
    },
    {
      number: 5,
      title: 'Body as an anchor - Body Scan',
      thumbnail: 'assets/img/memberdashboard/Rectangle 4811784 (4).png'
    }
  ];

  private readonly questThumbnail = 'assets/img/memberdashboard/Rectangle 4811782.png';
  private readonly questName = 'Mindfulness for Beginners';
  private readonly questDescription = '5 Sessions Series';

  constructor(private dashboard: MemberDashboardService, private elementRef: ElementRef) {
    const d = new Date();
    const pMindfulness = new Date(new Date().setDate(new Date().getDate() - 7));
    this.previousDateMindfulness = pMindfulness;
    this.currentDateMindfulness = d;
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const clickedElement = event.target as HTMLElement;
    // Check if the click is outside the dropdown container
    if (!clickedElement.closest('.completion-dropdown-container')) {
      this.closeAllDropdowns();
    }
  }

  closeAllDropdowns(): void {
    this.filteredMindfulnessCards.forEach(card => {
      card.sessions.forEach(session => {
        session.showDropdown = false;
      });
    });
  }

  ngOnInit(): void {
    // Use input dates if provided, otherwise use defaults
    if (this.startDate && this.endDate) {
      this.startDateMindfulness = this.startDate;
      this.endDateMindfulness = this.endDate;
      // Update date picker bound objects - parse yyyy-MM-dd format
      const startParts = this.startDate.split('-');
      const endParts = this.endDate.split('-');
      if (startParts.length === 3) {
        this.previousDateMindfulness = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]));
      } else {
        this.previousDateMindfulness = new Date(this.startDate);
      }
      if (endParts.length === 3) {
        this.currentDateMindfulness = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]));
      } else {
        this.currentDateMindfulness = new Date(this.endDate);
      }
    }
    // Load meditation data
    this.getMeditationData(this.startDateMindfulness, this.endDateMindfulness);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Watch for changes to startDate and endDate inputs
    let shouldReload = false;
    
    if (changes['startDate'] && changes['startDate'].currentValue) {
      this.startDateMindfulness = changes['startDate'].currentValue;
      // Parse date string to Date object for date picker binding
      // Handle yyyy-MM-dd format
      const dateParts = this.startDateMindfulness.split('-');
      if (dateParts.length === 3) {
        this.previousDateMindfulness = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
      } else {
        this.previousDateMindfulness = new Date(this.startDateMindfulness);
      }
      shouldReload = true;
    }
    
    if (changes['endDate'] && changes['endDate'].currentValue) {
      this.endDateMindfulness = changes['endDate'].currentValue;
      // Parse date string to Date object for date picker binding
      // Handle yyyy-MM-dd format
      const dateParts = this.endDateMindfulness.split('-');
      if (dateParts.length === 3) {
        this.currentDateMindfulness = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
      } else {
        this.currentDateMindfulness = new Date(this.endDateMindfulness);
      }
      shouldReload = true;
    }
    
    // Only reload data if both dates are available and we have changes
    if (shouldReload && this.startDateMindfulness && this.endDateMindfulness) {
      this.getMeditationData(this.startDateMindfulness, this.endDateMindfulness);
    }
  }

  filterByDateMindfulness(StartDate: HTMLInputElement, endDate: HTMLInputElement) {
    if (StartDate.value != '' && endDate.value != '') {
      this.startDateMindfulness = formatDate(StartDate.value, 'yyyy-MM-dd', 'en');
      this.endDateMindfulness = formatDate(endDate.value, 'yyyy-MM-dd', 'en');
      this.getMeditationData(this.startDateMindfulness, this.endDateMindfulness);
    }
  }

  getMeditationData(startDate: any, endDate: any) {
    this.isLoading = true;
    this.dashboard.getMeditationData(this.memberId, startDate, endDate).subscribe({
      next: (response) => {
        const apiData = (response as any).result || response;
        this.mindfulnessCards = this.transformApiDataToCards(apiData);
        this.applyFilters();
        this.expandedCardIndex = null; // Reset expanded state when new data loads
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching meditation data:', error);
        this.mindfulnessCards = [];
        this.filteredMindfulnessCards = [];
        this.isLoading = false;
      }
    });
  }

  private transformApiDataToCards(apiData: any): MindfulnessCard[] {
    const cards: MindfulnessCard[] = [];

    if (!apiData || typeof apiData !== 'object') {
      return cards;
    }

    // Iterate through each date in the API response
    Object.keys(apiData).forEach(dateKey => {
      const dateData = apiData[dateKey];
      if (!dateData || !dateData['Meditation ']) {
        return;
      }

      const meditationSessions = dateData['Meditation '];
      const sessions: MindfulnessSession[] = [];

      // Create all 5 sessions
      this.sessionConfig.forEach(config => {
        // Find ALL completions of this session type (not just the first one)
        const completedSessions = meditationSessions.filter((s: any) => 
          s.type === `Meditation Session ${config.number}`
        );

        // Map to SessionCompletion objects
        const completions: SessionCompletion[] = completedSessions.map((s: any) => ({
          id: s.id,
          createdDateTime: s.createdDateTime
        }));

        sessions.push({
          sessionNumber: config.number,
          title: config.title,
          thumbnail: config.thumbnail,
          completionDate: completions.length > 0 ? dateKey : null,
          isCompleted: completions.length > 0,
          completions: completions,
          showDropdown: false
        });
      });

      // Check if all sessions are completed
      const allCompleted = sessions.every(s => s.isCompleted);

      cards.push({
        dateKey: dateKey,
        startDate: dateKey,
        completedDate: allCompleted ? dateKey : null,
        isCompleted: allCompleted,
        sessions: sessions
      });
    });

    // Sort by date (newest first)
    return cards.sort((a, b) => {
      const dateA = new Date(a.dateKey).getTime();
      const dateB = new Date(b.dateKey).getTime();
      return dateB - dateA;
    });
  }

  searchMindfulness() {
    this.applyFilters();
  }

  filterByStatus(status: string) {
    this.statusFilter = status;
    this.applyFilters();
  }

  applyFilters() {
    let filtered = [...this.mindfulnessCards];

    // Apply search filter
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(card =>
        this.questName.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (this.statusFilter !== 'all') {
      filtered = filtered.filter(card => {
        const status = this.getCardStatus(card);
        if (this.statusFilter === 'in-progress') {
          return status === 'In-Progress';
        } else if (this.statusFilter === 'completed') {
          return status === 'Completed';
        }
        return true;
      });
    }

    this.filteredMindfulnessCards = filtered;
    this.expandedCardIndex = null; // Reset expanded state when filtering
  }

  toggleCardExpansion(index: number) {
    const wasExpanded = this.expandedCardIndex === index;
    this.expandedCardIndex = wasExpanded ? null : index;

    // Scroll to card when expanding
    if (!wasExpanded) {
      setTimeout(() => {
        const card = document.querySelectorAll('.quest-card')[index] as HTMLElement;
        if (card) {
          card.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 100);
    }
  }

  isCardExpanded(index: number): boolean {
    return this.expandedCardIndex === index;
  }

  getCardStatus(card: MindfulnessCard): string {
    if (card.isCompleted || card.completedDate) {
      return 'Completed';
    }
    return 'In-Progress';
  }

  getSessionStatus(session: MindfulnessSession): string {
    if (session.isCompleted) {
      return 'Completed';
    }
    return 'Not Started';
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  hasCards(): boolean {
    return this.filteredMindfulnessCards && this.filteredMindfulnessCards.length > 0;
  }

  getQuestThumbnail(): string {
    return this.questThumbnail;
  }

  getQuestName(): string {
    return this.questName;
  }

  getQuestDescription(): string {
    return this.questDescription;
  }

  toggleSessionDropdown(session: MindfulnessSession, event: Event): void {
    event.stopPropagation(); // Prevent card expansion
    const wasOpen = session.showDropdown;
    // Close all dropdowns first
    this.closeAllDropdowns();
    // Toggle the clicked dropdown (if it was closed, open it)
    session.showDropdown = !wasOpen;
  }

  hasCompletions(session: MindfulnessSession): boolean {
    return session.completions && session.completions.length >= 1;
  }

  formatTime(dateTimeString: string): string {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  }

  formatDateTimeForDropdown(dateTimeString: string): string {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: true 
    });
  }
}